import {
  Component, OnDestroy, OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../../../../shared/services';
import { ProjectsService } from '../../../../../core/projects.service';

@Component({
  selector: 'app-punchlist-wrapper',
  templateUrl: './punchlist-wrapper.component.html',
  styleUrls: ['./punchlist-wrapper.component.scss'],
  standalone: false,
})
export class PunchlistWrapperComponent implements OnInit, OnDestroy {
  activeSubTab: 'settings' | 'items' = 'settings';
  projectId: string;
  isContractor = false;
  punchlists: any[] = [];
  currentPunchlist: any = null;
  loading = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private projectsService: ProjectsService,
  ) {}

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    const account = this.authService.getCurrentUser();
    const role = account?.roles?.find((r: any) => r.project_id === this.projectId)?.role || account?.role || '';
    this.isContractor = role?.startsWith('contractor');

    this.fetchPunchlists();

    if (this.isContractor) this.activeSubTab = 'items';

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe((qp) => {
        // Only react when the URL explicitly carries a subTab value. Right-panel
        // close emits queryParams without subTab and we don't want to bounce the
        // user back to Settings.
        if (qp.subTab === 'items') {
          this.activeSubTab = 'items';
        } else if (qp.subTab === 'settings') {
          this.activeSubTab = 'settings';
        }
        if (qp.punchlistId) {
          const match = this.punchlists.find(p => (p.uuid || p.id) === qp.punchlistId);
          if (match) this.currentPunchlist = match;
        }
      });
  }

  fetchPunchlists(): void {
    this.loading = true;
    this.projectsService.fetchPunchlist({ projectId: this.projectId })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const list = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
          this.punchlists = list.map((r: any) => (
            r?.attributes ? { ...r.attributes, id: r.id, uuid: r.id } : r
          ));
          // Pick current punchlist from query param if present, else first
          const qpId = this.route.snapshot.queryParamMap.get('punchlistId');
          this.currentPunchlist = (qpId && this.punchlists.find(p => (p.uuid || p.id) === qpId))
            || this.punchlists[0]
            || null;
          this.loading = false;
        },
        error: () => { this.loading = false; },
      });
  }

  selectSubTab(subTab: 'settings' | 'items'): void {
    this.router.navigate([], {
      queryParams: { subTab, status: null },
      queryParamsHandling: 'merge',
    });
  }

  onPunchlistsChanged(): void {
    this.fetchPunchlists();
  }

  onSelectPunchlist(p: any): void {
    this.currentPunchlist = p;
    this.router.navigate([], {
      queryParams: { punchlistId: p.uuid || p.id, subTab: 'items' },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
